/**
 * Homeowner-facing service plan card.
 * Shows plan name, billing status, price, renewal date, and included features.
 * Offers "Manage Billing" link when Stripe billing portal is available.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard, ExternalLink, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { useHomeownerSubscription } from '@/hooks/useHomeownerSubscription';

const STATUS_DISPLAY: Record<string, { label: string; badge: string; message: string }> = {
  pending: {
    label: 'Pending',
    badge: 'bg-wt-amber-primary/20 text-wt-amber-primary',
    message: 'Billing setup pending. Your service provider is setting up billing.',
  },
  active: {
    label: 'Active',
    badge: 'bg-wt-green-primary/20 text-wt-green-primary',
    message: '',
  },
  trialing: {
    label: 'Trial',
    badge: 'bg-blue-500/20 text-blue-500',
    message: '',
  },
  past_due: {
    label: 'Payment Issue',
    badge: 'bg-wt-red-primary/20 text-wt-red-primary',
    message: 'Payment issue. Contact your service provider.',
  },
  canceled: {
    label: 'Canceled',
    badge: '',
    message: 'Plan canceled.',
  },
};

/** Capability keys visible to homeowners, mapped to readable labels. */
const HOMEOWNER_CAPABILITY_LABELS: Record<string, string> = {
  monitoring: 'System monitoring',
  forecast_basic: 'Basic forecast',
  forecast_extended: 'Extended forecast',
  notifications: 'Alerts',
  homeowner_portal: 'Homeowner portal',
  issue_submission: 'Issue submission',
  service_history: 'Service history',
  warranty_registry: 'Warranty tracking',
  customer_reports: 'Customer reports',
  upgrade_opportunities_homeowner: 'Upgrade insights',
};

interface HomeownerPlanCardProps {
  propertyId: string;
}

export function HomeownerPlanCard({ propertyId }: HomeownerPlanCardProps) {
  const { data: sub, isLoading } = useHomeownerSubscription(propertyId);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const result = await invokeAuthedFunction<{ portal_url?: string; error?: string }>(
        supabase,
        'manage-customer-billing',
        { property_id: propertyId },
      );
      if (result.error) {
        toast.error(result.error);
      } else if (result.portal_url) {
        window.open(result.portal_url, '_blank');
      }
    } catch {
      toast.error('Unable to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-36" />
        </CardContent>
      </Card>
    );
  }

  if (!sub) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4" />
            Service Plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No service plan assigned. Contact your service provider.
          </p>
        </CardContent>
      </Card>
    );
  }

  const status = STATUS_DISPLAY[sub.status] ?? STATUS_DISPLAY.canceled;
  const priceDollars = ((sub.price_override_cents ?? sub.plan_price_cents) / 100).toFixed(2);
  const intervalLabel = sub.plan_billing_interval === 'annual' ? '/yr' : '/mo';

  const renewalDate = sub.current_period_end
    ? new Date(sub.current_period_end).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const canManageBilling =
    !!sub.stripe_connect_subscription_id &&
    !!sub.stripe_connect_customer_id &&
    ['active', 'trialing', 'past_due'].includes(sub.status);

  // Filter capabilities to homeowner-visible ones
  const features = (sub.capabilities ?? [])
    .filter((cap: string) => cap in HOMEOWNER_CAPABILITY_LABELS)
    .map((cap: string) => HOMEOWNER_CAPABILITY_LABELS[cap]);

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4" />
            Service Plan
          </CardTitle>
          <Badge variant="default" className={status.badge}>
            {status.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Plan name and price */}
        <div>
          <p className="text-base font-medium">{sub.plan_name}</p>
          {sub.plan_description && (
            <p className="text-xs text-muted-foreground mt-0.5">{sub.plan_description}</p>
          )}
          <p className="text-sm font-mono mt-1">
            ${priceDollars}
            <span className="text-muted-foreground text-xs">{intervalLabel}</span>
          </p>
        </div>

        {/* Status message */}
        {status.message && (
          <p className="text-sm text-muted-foreground">{status.message}</p>
        )}

        {/* Renewal date */}
        {renewalDate && (sub.status === 'active' || sub.status === 'trialing') && (
          <p className="text-xs text-muted-foreground">
            {sub.cancel_at_period_end ? 'Ends' : 'Renews'} {renewalDate}
          </p>
        )}

        {/* Service provider */}
        {sub.provider_name && (
          <p className="text-xs text-muted-foreground">
            Managed by {sub.provider_name}
          </p>
        )}

        {/* Included features */}
        {features.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Included features</p>
            <div className="flex flex-wrap gap-1">
              {features.map((label: string) => (
                <Badge key={label} variant="outline" className="text-[10px]">
                  {label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Manage Billing button */}
        {canManageBilling && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleManageBilling}
            disabled={portalLoading}
          >
            {portalLoading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4 mr-1.5" />
            )}
            Manage Billing
            <ExternalLink className="w-3 h-3 ml-1.5" />
          </Button>
        )}
        {!canManageBilling && sub.status !== 'canceled' && sub.status !== 'pending' && (
          <p className="text-xs text-muted-foreground">
            Billing managed by your service provider.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Re-export for tests
export { HOMEOWNER_CAPABILITY_LABELS };
